import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faAddressCard,
  faGraduationCap,
  faWallet,
  faVideo,
  faCamera,
  faCropSimple,
  faTrashCan,
  faChalkboardUser,
  faArrowUpRightFromSquare,
  faFloppyDisk,
  faCircleCheck,
  faTriangleExclamation,
  faUserGraduate,
  faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import ImageCropper from '../components/ImageCropper/ImageCropper';
import StartCourse from './StartCourse';
import './EditStudentInfo.css';

const API = process.env.REACT_APP_API_BASE_URL;
const DEFAULT_IMG = `${API}/assets/students/student.jpg`; // same default as before
const v = (x) => (x === null || x === undefined ? '' : x); // show empty inputs, keep the saved value untouched

/* Paid student: same fields, same FormData, same endpoint as before.
   + Assigned teacher (moved here from the old "Teacher" tab, same endpoint). */
const EditStudentInfo = ({ student, setStudent, teachers = [], assignedTeacher = '', onTeacherAssigned }) => {
  const [formData, setFormData] = useState({
    firstName: student.FirstName,
    lastName: student.LastName,
    email: student.ParentsEmail,
    mobileNumber: student.MobileNumber,
    birthday: student.Birthday ? String(student.Birthday).split('T')[0] : '',
    image: student.Image,
    username: student.Username,
    age: student.Age,
    grade: student.Grade,
    IdGradesPlans: student.IdGradesPlans,
    Type: student.Type === 0 ? 'Paid' : 'Demo',
    CodeJoinDemo: student.CodeJoinDemo,
    SessionFees: student.SessionFees,
    SessionRate: student.SessionRate,
    ZoomLink: student.ZoomLink,
    Offset: student.Offset,
  });
  const [OffsetStudent, setOffsetStudent] = useState(student.Offset);
  const [OffsetStudentList, setOffsetStudentList] = useState([]);
  const [listError, setListError] = useState('');

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(student.ImageUrl1 || DEFAULT_IMG);
  const [imgBroken, setImgBroken] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // teacher assignment
  const [teacherId, setTeacherId] = useState(assignedTeacher || '');
  const [sendCongrats, setSendCongrats] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignMsg, setAssignMsg] = useState(null); // { ok, text }

  const studentId = student?.IdUser;

  useEffect(() => setTeacherId(assignedTeacher || ''), [assignedTeacher]);

  // ---------- Time zones (same endpoint), once per student ----------
  useEffect(() => {
    if (!studentId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/studentprofile/OffsetList`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` },
        });
        if (!cancelled) setOffsetStudentList(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Error fetching timezones:', err);
        if (!cancelled) setListError('Couldn’t load the time zones.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(false), 3000);
    return () => clearTimeout(t);
  }, [success]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
    setSuccess(false);
  };

  // Pick a photo -> crop it (square, round guide) -> the cropped photo is uploaded on Save
  // The photo BEFORE the crop is kept too (on the server), so a bad crop can be undone with "Back to original".
  const [cropSrc, setCropSrc] = useState(null);
  const [pickedRaw, setPickedRaw] = useState(null); // { file, url } just picked, not cropped yet
  const [rawPhoto, setRawPhoto] = useState(null); // { file, url } new photo before the crop (sent on Save)
  const [restoreOriginal, setRestoreOriginal] = useState(false);
  const originalUrl = student.ImageOriginalUrl || null; // saved photo before its crop

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setPickedRaw({ file, url: reader.result });
      setCropSrc(reader.result);
    };
    reader.readAsDataURL(file);
    e.target.value = ''; // lets you pick the same file again
  };
  const onCropped = (file) => {
    if (pickedRaw) setRawPhoto(pickedRaw);
    setPickedRaw(null);
    setRestoreOriginal(false);
    setImageFile(file);
    setImgBroken(false);
    setImagePreview(URL.createObjectURL(file));
    setCropSrc(null);
    setSuccess(false);
  };
  const cancelCrop = () => {
    setPickedRaw(null);
    setCropSrc(null);
  };
  // crop again from the full photo (never a crop of a crop)
  const cropAgain = () => setCropSrc(rawPhoto?.url || (!imageFile && originalUrl) || imagePreview);
  const backToOriginal = () => {
    setImageFile(null);
    setRawPhoto(null);
    setRestoreOriginal(true);
    setImgBroken(false);
    setImagePreview(originalUrl);
    setSuccess(false);
  };
  // re-crop the photo the student already has (or the new one picked)
  const hasRealPhoto = !imgBroken && imagePreview && imagePreview !== DEFAULT_IMG && !/\/student\.jpg$/.test(imagePreview);

  const handleRemoveImage = () => {
    setImageFile(null);
    setRawPhoto(null);
    setRestoreOriginal(false);
    setImgBroken(false);
    setImagePreview(DEFAULT_IMG);
    setFormData((f) => ({ ...f, image: 'student.jpg' }));
  };

  // ---------- Save (same endpoint + same FormData fields) ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    try {
      const fd = new FormData();
      fd.append('firstName', formData.firstName);
      fd.append('lastName', formData.lastName);
      fd.append('email', formData.email);
      fd.append('mobileNumber', formData.mobileNumber);
      fd.append('birthday', formData.birthday || '');
      fd.append('username', formData.username);
      fd.append('age', formData.age);
      fd.append('grade', formData.grade);
      fd.append('IdGradesPlans', formData.IdGradesPlans);
      fd.append('CodeJoinDemo', formData.CodeJoinDemo);
      fd.append('SessionFees', formData.SessionFees);
      fd.append('SessionRate', formData.SessionRate);
      fd.append('ZoomLink', formData.ZoomLink);
      fd.append('Offset', OffsetStudent);
      if (imageFile) {
        fd.append('image', imageFile);
        // the photo before the crop (only the types and size the server accepts)
        const raw = rawPhoto?.file;
        if (raw && raw.size <= 5 * 1024 * 1024 && /^image\/(jpe?g|png|gif)$/i.test(raw.type)) fd.append('original', raw);
      } else if (restoreOriginal) fd.append('restoreOriginal', '1');
      else if (formData.image === 'student.jpg') fd.append('imageName', 'student.jpg');

      const res = await axios.put(`${API}/api/admin/studentprofile/update/${student.IdUser}`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem('userData')}`, 'Content-Type': 'multipart/form-data' },
      });
      const saved = res.data;
      if (saved && typeof saved === 'object' && 'FirstName' in saved) {
        setStudent(saved);
      } else {
        // keep the profile working if the server doesn't send the student back
        setStudent((s) => ({
          ...s,
          FirstName: formData.firstName,
          LastName: formData.lastName,
          ParentsEmail: formData.email,
          MobileNumber: formData.mobileNumber,
          Birthday: formData.birthday || null,
          Username: formData.username,
          Age: formData.age,
          Grade: formData.grade,
          IdGradesPlans: Number(formData.IdGradesPlans) || formData.IdGradesPlans,
          SessionFees: formData.SessionFees,
          SessionRate: formData.SessionRate,
          ZoomLink: formData.ZoomLink,
          Offset: OffsetStudent,
        }));
      }
      setImageFile(null);
      setRawPhoto(null);
      setRestoreOriginal(false);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update student');
    } finally {
      setLoading(false);
    }
  };

  // ---------- Assign teacher (same endpoint + body as the old Teacher tab) ----------
  const assignTeacher = async () => {
    setAssigning(true);
    setAssignMsg(null);
    try {
      const res = await axios.post(
        `${API}/api/admin/studentprofile/assignteacher/${studentId}`,
        { teacherId, sendCongrats },
        { headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } }
      );
      const saved = res.data?.teacherId ?? teacherId;
      setAssignMsg({ ok: true, text: 'Teacher assigned.' });
      if (onTeacherAssigned) onTeacherAssigned(saved);
    } catch (err) {
      setAssignMsg({ ok: false, text: err.response?.data?.error || err.message || 'Couldn’t assign the teacher.' });
    } finally {
      setAssigning(false);
    }
  };

  const currentTeacher = teachers.find((t) => String(t.id) === String(assignedTeacher));
  const teacherChanged = String(teacherId || '') !== String(assignedTeacher || '');

  const field = (name, label, type = 'text', { disabled, ...extra } = {}) => (
    <label className="rmaes-field">
      <span>
        {label}
        {extra.required && <b aria-hidden="true"> *</b>}
      </span>
      <input type={type} name={name} value={v(formData[name])} onChange={handleChange} disabled={loading || !!disabled} {...extra} />
    </label>
  );

  return (
    <div className="rmaes">
      {/* ---------- Assigned teacher ---------- */}
      <section className="rmaes-card rmaes-teacher">
        <h3>
          <FontAwesomeIcon icon={faChalkboardUser} /> Assigned teacher
        </h3>
        <div className="rmaes-teacher-row">
          <div className="rmaes-current">
            <small>Current</small>
            {currentTeacher ? (
              <a href={`/Admin/teacherprofile/${assignedTeacher}`} target="_blank" rel="noopener noreferrer">
                {currentTeacher.name} <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              </a>
            ) : (
              <span className="rmaes-dim">No teacher yet</span>
            )}
          </div>
          <label className="rmaes-field rmaes-grow">
            <span>Change to</span>
            <select value={v(teacherId)} onChange={(e) => { setTeacherId(e.target.value); setAssignMsg(null); }} disabled={assigning}>
              <option value="">Select teacher</option>
              {teachers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <label className="rmaes-check">
            <input type="checkbox" checked={sendCongrats} onChange={(e) => setSendCongrats(e.target.checked)} disabled={assigning} />
            <span>Send congratulations email</span>
          </label>
          <button type="button" className="rmaes-btn" onClick={assignTeacher} disabled={!teacherId || assigning || (!teacherChanged && !sendCongrats)}>
            <FontAwesomeIcon icon={faChalkboardUser} /> {assigning ? 'Saving…' : 'Save teacher'}
          </button>
        </div>
        {assignMsg && (
          <p className={`rmaes-msg ${assignMsg.ok ? 'is-ok' : 'is-error'}`}>
            <FontAwesomeIcon icon={assignMsg.ok ? faCircleCheck : faTriangleExclamation} /> {assignMsg.text}
          </p>
        )}
      </section>

      {/* ---------- Starting course ---------- */}
      <StartCourse studentId={student.IdUser} />

      <form onSubmit={handleSubmit}>
        <div className="rmaes-grid">
          {/* ---------- Photo ---------- */}
          <section className="rmaes-card rmaes-photo-card">
            <h3>
              <FontAwesomeIcon icon={faCamera} /> Photo
            </h3>
            <div className="rmaes-photo">
              <span className="rmaes-preview">
                {imagePreview && !imgBroken ? <img src={imagePreview} alt="" onError={() => setImgBroken(true)} /> : <FontAwesomeIcon icon={faUserGraduate} />}
              </span>
              <div className="rmaes-photo-actions">
                <label className="rmaes-btn rmaes-btn--ghost">
                  <input type="file" accept="image/*" onChange={handleImageChange} disabled={loading} hidden />
                  <FontAwesomeIcon icon={faCamera} /> {imageFile ? 'Choose another' : 'Change photo'}
                </label>
                {hasRealPhoto && (
                  <button type="button" className="rmaes-btn rmaes-btn--ghost" onClick={cropAgain} disabled={loading}>
                    <FontAwesomeIcon icon={faCropSimple} /> Crop
                  </button>
                )}
                {originalUrl && !restoreOriginal && !rawPhoto && (
                  <button type="button" className="rmaes-btn rmaes-btn--ghost" onClick={backToOriginal} disabled={loading} title="Show the photo as it was before the crop">
                    <FontAwesomeIcon icon={faRotateLeft} /> Back to original
                  </button>
                )}
                <button type="button" className="rmaes-btn rmaes-btn--ghost rmaes-btn--danger" onClick={handleRemoveImage} disabled={loading}>
                  <FontAwesomeIcon icon={faTrashCan} /> Remove
                </button>
                <small>
                  {imageFile
                    ? 'New photo ready: saved when you press Save changes'
                    : restoreOriginal
                    ? 'Original photo: saved when you press Save changes'
                    : 'Max 5MB · JPG, PNG or GIF · you can crop it, the original photo is kept'}
                </small>
              </div>
            </div>
            {cropSrc && <ImageCropper src={cropSrc} title="Crop the student’s photo" onCancel={cancelCrop} onDone={onCropped} />}
          </section>

          {/* ---------- Personal ---------- */}
          <section className="rmaes-card">
            <h3>
              <FontAwesomeIcon icon={faUser} /> Student
            </h3>
            <div className="rmaes-row">
              {field('firstName', 'First name', 'text', { required: true, autoComplete: 'off' })}
              {field('lastName', 'Last name', 'text', { required: true, autoComplete: 'off' })}
            </div>
            <div className="rmaes-row rmaes-row--3">
              {field('birthday', 'Birthday', 'date')}
              {field('age', 'Age', 'number', { min: 3, max: 99 })}
              {field('grade', 'Grade')}
            </div>
          </section>

          {/* ---------- Contact & account ---------- */}
          <section className="rmaes-card">
            <h3>
              <FontAwesomeIcon icon={faAddressCard} /> Contact & account
            </h3>
            {field('email', 'Parent email', 'email', { required: true, autoComplete: 'off' })}
            <div className="rmaes-row">
              {field('mobileNumber', 'Mobile number', 'tel', { required: true, autoComplete: 'off' })}
              {field('username', 'Username', 'text', { required: true, autoComplete: 'off' })}
            </div>
          </section>

          {/* ---------- Program ---------- */}
          <section className="rmaes-card">
            <h3>
              <FontAwesomeIcon icon={faGraduationCap} /> Program
            </h3>
            <div className="rmaes-row">
              <label className="rmaes-field">
                <span>Grade plan</span>
                <select name="IdGradesPlans" value={v(formData.IdGradesPlans)} onChange={handleChange} disabled={loading}>
                  <option value="1">Little</option>
                  <option value="2">Junior</option>
                </select>
              </label>
              <label className="rmaes-field">
                <span>Student type</span>
                <input type="text" value={formData.Type} disabled readOnly />
              </label>
            </div>
            {field('CodeJoinDemo', 'Demo join code', 'text', { disabled: formData.Type === 'Paid' })}
          </section>

          {/* ---------- Money ---------- */}
          <section className="rmaes-card">
            <h3>
              <FontAwesomeIcon icon={faWallet} /> Fees
            </h3>
            <div className="rmaes-row">
              {field('SessionFees', 'Session fees ($)', 'number', { step: '0.01', min: '0', disabled: formData.Type === 'Demo', inputMode: 'decimal' })}
              {field('SessionRate', 'Session rate ($/hr)', 'number', { step: '0.01', min: '0', disabled: formData.Type === 'Demo', inputMode: 'decimal' })}
            </div>
            <p className="rmaes-hint">Fees are what the student pays per session; the rate is what the teacher earns.</p>
          </section>

          {/* ---------- Class ---------- */}
          <section className="rmaes-card">
            <h3>
              <FontAwesomeIcon icon={faVideo} /> Class
            </h3>
            {field('ZoomLink', 'Zoom link', 'text', { placeholder: 'https://zoom.us/j/…' })}
            <label className="rmaes-field">
              <span>Time zone</span>
              <select value={v(OffsetStudent)} onChange={(e) => setOffsetStudent(e.target.value)} disabled={loading}>
                <option value="">Select time zone</option>
                {OffsetStudentList.map((o) => (
                  <option key={o.Id} value={o.Id}>
                    {o.Timezone} ({o.TimeHours >= 0 ? '+' : ''}
                    {o.TimeHours})
                  </option>
                ))}
              </select>
              {listError && <small className="is-error">{listError}</small>}
            </label>
          </section>
        </div>

        {/* ---------- Save bar ---------- */}
        <div className="rmaes-bar" aria-live="polite">
          <p className={error ? 'is-error' : success ? 'is-ok' : ''}>
            {error ? (
              <>
                <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
              </>
            ) : success ? (
              <>
                <FontAwesomeIcon icon={faCircleCheck} /> Student information updated.
              </>
            ) : (
              'Changes to the student are saved together.'
            )}
          </p>
          <button type="submit" className="rmaes-btn" disabled={loading}>
            <FontAwesomeIcon icon={faFloppyDisk} /> {loading ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditStudentInfo;